/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-unused-prop-types */
import { createUseStyles } from '@glu/theming';
import React, { useMemo } from 'react';
import { withInputAPI, useInputAPI } from '@glu/validation-react';
import { LabelText, Error } from '@glu/form-components';
import Panel, { PanelActions } from './Panel';
import SelectField from './SelectField';
import Tooltip from './Tooltip';
import styles from './Dropdown.styles';
import { CustomOptionProps } from './Options/Options';
import useDropdownState from './useDropdownState';
import DropdownContext, { DropdownTypes, IDropdownContext, State } from './DropdownContext';
import { internalFormat } from './utils/formatOptions';
import { PanelState } from './PanelContext';

const useStyles = createUseStyles(styles);

export interface PanelOption {
  child?: PanelOption[];
  depth?: number;
  disabled?: boolean;
  highlight?: boolean;
  id: string | number;
  name: string;
  parentName?: string;
  path?: Path[];
}

export interface Path {
  id: string | number;
  name: string;
}

export interface CustomOptionsComponent {
  applySelection?: DropdownActions['applySelection'];
  filterString?: PanelState['filterString'];
  focusedIndex?: State['focusedIndex'];
  handleCategorySelect?: PanelActions['handleCategorySelect'];
  htmlId?: DropdownProps['htmlId'];
  multiselect?: DropdownProps['multiselect'];
  /** callback to handle when custom option component is clicked */
  onClick?: () => void;
  optionComponent?: DropdownProps['optionComponent'];
  options: DropdownProps['options'];
  /** List of options representing the breadcrumb path */
  path?: Path[];
  selection?: State['selection'];
  setArrowDirection?: PanelActions['setArrowDirection'];
  setFocusedIndex?: DropdownActions['setFocusedIndex'];
  setIsActionsFocused?: PanelActions['setIsActionsFocused'];
  setIsFilterFocused?: DropdownActions['setIsFilterFocused'];
  setParentName?: PanelActions['setParentName'];
  setPath?: PanelActions['setPath'];
  setSelection?: DropdownActions['setSelection'];
  showGroups?: DropdownProps['showGroups'];
}

type TWithInputProps = Parameters<typeof useInputAPI>['0'];
export interface DropdownProps extends Omit<TWithInputProps, 'name' | 'onChange' | 'onBlur'> {
  ariaLabel?: string;
  /** Toggle "Clear" button to clear current selection */
  btnClearEnabled?: boolean;
  /** Toggle if done button is visible. Actively selecting done is not required. */
  btnDoneEnabled?: boolean;
  /** Toggle "Select All" button to select all available rows at the current level */
  btnSelectAllEnabled?: boolean;
  children?: React.ReactNode;
  /** Class passed to parent element for extra decoration. */
  className?: string;
  /** String given to parent elements data-qa html attribute for easy targeting */
  dataQa?: string;
  /** Disable the dropdown entirely */
  disabled?: boolean;
  /** Custom message to show in the select when there are no options to display */
  emptyOptionsMessage?: string;
  /** Error messages. Will add styles to input if present. */
  errors?: string[];
  /* String defining what filtering algorithm to use.
   * Defaults to "contains" if none provided
   * Only use contains or startsWith unless providing a custom filterFunction.
   */
  filterBy?: 'contains' | 'startsWith' | string;
  /**
   * Function to call when filtering is desired. It will be passed a function that,
   * when called will perform the actions of filtering the list.
   * Useful for creating a custom debounce function
   */
  filterDebounce?: (filterFunc: () => void) => void;
  /** Whether to allow filtering by text input */
  filterEnabled?: boolean;
  /** Custom filter function to be used instead of default */
  // eslint-disable-next-line max-len
  filterFunction?: (
    options: PanelOption[],
    filterString: string,
    filterBy: string,
    matchBy: string[]
  ) => PanelOption[];
  /** Placeholder text to display in filter input */
  filterPlaceholder?: string;
  /**
   * UX setting to make the dropdown fit the parent element's width.
   * If the selected items do not fit within the dropdown's width they will
   * be wrapped to new lines.
   */
  fitParentWidth?: boolean;
  /** used to control the open state by another higher component */
  forceOpen?: boolean;
  forcePositionY?: boolean;
  /** Function receives selection array as a parameter and is expected to return a string.
   * Used for changing the format of the string value in the text input. */
  format?: typeof internalFormat;
  /** string assigned to HTML id attribute of component */
  /** Component provided for a custom header.
   * Will render as the first item before the options and filter. */
  headerComponent?: (props: unknown) => JSX.Element;
  htmlId?: string;
  /** Boolean to show loading spinner */
  isLoading?: boolean;
  /** String used for label value */
  labelText?: string | JSX.Element;
  /** What properties to match on when filtering */
  matchBy?: string[];
  /** Number of minimum selected items to show tooltip */
  minSelectedForTooltip?: number;
  /** Allow multiple selections of dropdown options */
  multiselect?: boolean;
  /** string passed to html name attribute of text input */
  name?: string;
  /** Callback when panel is closed after being open */
  onBlur?: (name?: DropdownProps['name'], value?: string | number, fromBlur?: boolean) => void;
  /** Callback when a category is selected */
  onCategorySelect?: (value?: PanelOption) => void;
  /** Callback when a date is selected or changed */
  onChange?: (name: string, selection: PanelOption[], value: boolean) => void;
  /** Callback when "Clear" button clicked */
  onClear?: () => void;
  /** Callback when the panel is closed. */
  onClose?: () => void;
  /** Callback for when "Done" is clicked */
  onDone?: (name: string, value: PanelOption[]) => void;
  /** Callback when user inputs new filter value */
  onFilter?: (value: string) => void;
  /** Callback that is called when the user opens the dropdown for the first time */
  onFirstOpen?: () => void;
  /** Callback when the next page of options are requested (usually after scrolling) */
  onNextPage?: (filter?: string) => void;
  /** Callback that is called when the user opens the dropdown */
  onOpen?: () => void;
  /** Callback when "Select All" button clicked */
  onSelectAll?: () => void;
  /** React component which will render the option item */
  optionComponent?: (props: CustomOptionProps) => JSX.Element;
  /** React component which will render all the options */
  optionListComponent?: (props: CustomOptionsComponent) => JSX.Element;
  /** Show or hide the "optional" locale text */
  optional?: boolean;
  /** Data object used to generate options
   * expected format:
   * [{id: 123, name: 'foobar', child: array}] */
  options?: PanelOption[];
  /** Placeholder text for the select button when no selection has been made */
  placeholder?: string;
  positionX?: string;
  positionY?: string;
  /** First option is selected.
   * Overrides preSelectedIds and preSelectedNames. */
  preSelectFirstOption?: boolean;
  /** Array of option ids to include in selection */
  preSelectedIds?: string[] | number[];
  /** Array of option names to include in selection */
  preSelectedNames?: string[];
  /** Number of rows to load at a time */
  rowsPerPage?: number;
  /** Toggle to hide the label for all but screen readers */
  screenReaderLabel?: boolean;
  scroll?: boolean;
  /** Toggle if selected items should display as badges */
  showBadges?: boolean;
  /** Display the group of the selected value in the input. Only available for single select */
  showGroupInSelection?: boolean;
  /** Toggle to display child elements as a flat list with parents as headers */
  showGroups?: boolean;
  /** Number of options to show in the select field before fallback to "N selected" message* */
  showSelected?: number;
  /** Toggle display of tooltip which appears on hover */
  showTooltip?: boolean;
  /** Options which will be selected when component loads.
   * Overrides other preselect settings  */
  value?: PanelOption[];
}

export interface DropdownActions {
  /** Function to be called when the user is done */
  applySelection: (value: PanelOption[]) => void;
  closeDropdown: () => void;
  handleClose: () => void;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  onBlur: () => void;
  openDropdown: (value: boolean) => void;
  setFocusedIndex: (payload: number) => void;
  setIsFilterFocused: (payload: boolean) => void;
  setPressedKey: (payload: string) => void;
  /** Function to set state of selection object */
  setSelection: (payload: PanelOption[]) => void;
}

const Dropdown = (props: DropdownProps) => {
  const classes = useStyles();
  const {
    applySelection,
    ariaLabel,
    closeDropdown,
    dispatch,
    divRef,
    error,
    handleClose,
    handleKeyDown,
    onBlur,
    openDropdown,
    props: dropdownProps,
    setFocusedIndex,
    setIsFilterFocused,
    setPressedKey,
    setSelection,
    state,
    uniqueId,
  } = useDropdownState(props);

  const {
    className,
    dataQa,
    disabled,
    format = internalFormat,
    htmlId,
    labelText,
    minSelectedForTooltip,
    optional,
    screenReaderLabel,
    showTooltip,
  } = dropdownProps;

  const { open, parentDisabled, selection, tooltipOpen } = state;

  const dropdownContext: IDropdownContext = useMemo(
    () => ({
      ariaLabel,
      divRef,
      state,
      props: { ...dropdownProps },
      uniqueId,
      dropdownActions: {
        setFocusedIndex,
        setIsFilterFocused,
        setPressedKey,
        setSelection,
        handleClose,
        handleKeyDown,
        applySelection,
        openDropdown,
        closeDropdown,
        onBlur,
      },
    }),
    [
      applySelection,
      closeDropdown,
      divRef,
      dropdownProps,
      handleClose,
      handleKeyDown,
      openDropdown,
      setFocusedIndex,
      setIsFilterFocused,
      setPressedKey,
      setSelection,
      state,
      uniqueId,
      onBlur,
    ]
  );

  return (
    <DropdownContext.Provider value={dropdownContext}>
      <div
        ref={divRef}
        className={`${classes.root} ${className}`}
        data-qa={dataQa ? `${dataQa}` : `dropdown-${htmlId}`}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => {
          if (showTooltip) {
            dispatch({ type: DropdownTypes.SET_TOOLTIP_OPEN, payload: true });
          }
        }}
        onMouseLeave={() => {
          if (showTooltip) {
            dispatch({ type: DropdownTypes.SET_TOOLTIP_OPEN, payload: false });
          }
        }}
        role="presentation"
      >
        <label htmlFor={`${uniqueId}-select-field`} id={`${uniqueId}-select-field-label`}>
          <LabelText
            hasError={!!error}
            label={labelText}
            optional={optional}
            screenReaderLabel={screenReaderLabel}
          />
        </label>
        <div className={classes.wrapper} data-disabled={parentDisabled || disabled}>
          <SelectField />
          {open && <Panel />}
          {selection.length > 0 &&
            showTooltip &&
            minSelectedForTooltip <= selection.length &&
            tooltipOpen &&
            !open && <Tooltip format={format} selection={selection} />}
        </div>
        <Error error={error} />
      </div>
    </DropdownContext.Provider>
  );
};

const InputApiDropdown: React.ComponentType<DropdownProps> = withInputAPI(Dropdown);
InputApiDropdown.displayName = 'Dropdown';
export default InputApiDropdown;
export const BareDropdown = Dropdown;
